import { forwardRef, useMemo, type ComponentRef, type Ref } from "react";

import { cn, platformProps } from "../../lib";
import { webOnly } from "../../lib/web-role";
import { Box, Pressable } from "../../primitives";
import { CardContext } from "./card-context";
import { cardPressableClassName, cardRootVariants } from "./lib";
import type { CardContextValue, CardProps } from "./types";

// The root: the Pressable's element when the card has `onPress`, a Box's otherwise
export const CardRoot = forwardRef<ComponentRef<typeof Box>, CardProps>(function CardRoot(
  {
    state = "idle",
    direction = "col",
    layout = "default",
    radius = "lg",
    shadow = "none",
    gap,
    onPress,
    accessibilityRole = "button",
    checked,
    disabled: disabledProp = false,
    className,
    testID,
    accessibilityLabel,
    children,
  },
  ref,
) {
  const disabled = disabledProp || state === "disabled";
  const isRadio = accessibilityRole === "radio";

  const contextValue = useMemo<CardContextValue>(
    () => ({ state, direction, disabled }),
    [state, direction, disabled],
  );

  // A pressable card is a control, so it's at least a touch target tall. On
  // web it's a `<button>`, which block layout sizes to its content, so it
  // fills the width as the Box's `div` does; a caller's width class wins.
  const rootClassName = cn(
    cardRootVariants({
      direction,
      layout,
      state,
      radius,
      shadow,
      // Figma spaces a row's slots 8 and a column's 12
      gap: gap ?? (direction === "row" ? "sm" : "md"),
    }),
    onPress && "min-h-touch-target",
    onPress && webOnly("w-full"),
    onPress && !disabled && cardPressableClassName,
    className,
  );

  return (
    <CardContext.Provider value={contextValue}>
      {onPress ? (
        <Pressable
          ref={ref as Ref<ComponentRef<typeof Pressable>>}
          onClick={onPress}
          disabled={disabled}
          accessibilityRole={accessibilityRole}
          accessibilityState={isRadio ? { disabled, checked: checked ?? false } : { disabled }}
          accessibilityLabel={accessibilityLabel}
          className={rootClassName}
          {...platformProps({ testID, slot: "card" })}
        >
          {children}
        </Pressable>
      ) : (
        <Box
          ref={ref}
          accessibilityLabel={accessibilityLabel}
          className={rootClassName}
          {...platformProps({ testID, slot: "card" })}
        >
          {children}
        </Box>
      )}
    </CardContext.Provider>
  );
});

CardRoot.displayName = "Card";
